import { useContext, useEffect, useState } from "react";
import TenantContext from "../../context/tenant/TenantContext";
import useTranslator from "../../hooks/useTranslator";
import OccupantForm, {
  buildOccupantsPayload,
  emptyOccupant,
  isEmptyOccupant,
  toOccupantForm,
  validateOccupant,
} from "../../components/Occupants/OccupantForm";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import { Button, Icon } from "../../components/design";
import occupantStyles from "../../components/Occupants/Occupants.module.css";
import styles from "./BasicInfoModal.module.css";

// Company login, last onboarding step: the people who will live in the
// property. Optional: the company can finish without adding anyone and add
// occupants later from the profile page.

const OCCUPANTS_STEP = 11;

const OccupantsStep = ({
  user,
  formHandler,
  updateUserProfile,
  onComplete,
}) => {
  const { _t } = useTranslator();
  const { uploadFileData } = useContext(TenantContext);
  // Always one occupant open; an untouched one counts as skipped.
  const [occupants, setOccupants] = useState([emptyOccupant()]);
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (user?.occupants?.length) {
      setOccupants(user.occupants.map(toOccupantForm));
    }
  }, [user?._id]);

  const updateOccupant = (index, changes) => {
    setOccupants((prev) =>
      prev.map((occupant, i) =>
        i === index ? { ...occupant, ...changes } : occupant
      )
    );
    setErrors((prev) => ({ ...prev, [index]: undefined }));
  };

  const removeOccupant = (index) => {
    occupants[index].files.forEach((file) => URL.revokeObjectURL(file.preview));
    setOccupants((prev) => {
      const next = prev.filter((_, i) => i !== index);
      return next.length ? next : [emptyOccupant()];
    });
    setErrors({});
  };

  const handleSubmit = async () => {
    const newErrors = {};
    occupants.forEach((occupant, index) => {
      if (isEmptyOccupant(occupant)) return;
      const occupantErrors = validateOccupant(occupant, _t);
      if (Object.keys(occupantErrors).length) newErrors[index] = occupantErrors;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setSubmitLoading(true);
    try {
      await updateUserProfile({
        occupants: await buildOccupantsPayload(
          occupants.filter((occupant) => !isEmptyOccupant(occupant)),
          uploadFileData
        ),
        step: OCCUPANTS_STEP,
        role: "tenant",
      });
      await onComplete();
    } catch (err) {
      console.error("Error saving occupants:", err);
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      <div className={styles.progressSpacer} aria-hidden="true" />
      <div className={`${styles.canvas} ${occupantStyles.stepCanvas}`}>
        <div className={styles.cardShell}>
          <form
            className={`${styles.card} ${occupantStyles.stepForm}`}
            onSubmit={(e) => e.preventDefault()}
          >
            <AuthStepHeader
              onBack={formHandler.prev}
              title={_t(
                "whoWillLiveHere",
                "Onboarding sidebar step for company tenants: people who will live in the property"
              )}
              subtitle={_t(
                "whoWillLiveHereDescription",
                "Occupants step subtitle: asks the company to add every person who will live in the property with their ID"
              )}
            />

            {/* Occupants scroll; the buttons below stay in view. */}
            <div className={occupantStyles.list}>
              {occupants.map((occupant, index) => (
                <OccupantForm
                  key={index}
                  index={index}
                  occupant={occupant}
                  errors={errors[index]}
                  onChange={(changes) => updateOccupant(index, changes)}
                  heading={`${_t(
                    "occupant",
                    "Occupants step: heading of each occupant block, followed by its number"
                  )} ${index + 1}`}
                  headerAction={
                    <Button
                      variant="dangerGhost"
                      size="sm"
                      iconStart={<Icon name="trash" size={16} />}
                      onClick={() => removeOccupant(index)}
                    >
                      {_t(
                        "removeOccupant",
                        "Occupants step: button to remove an occupant block"
                      )}
                    </Button>
                  }
                />
              ))}
            </div>

            <div className={occupantStyles.stepFooter}>
              <Button
                variant="outline"
                block
                iconStart={<Icon name="plus" size={16} />}
                onClick={() =>
                  setOccupants((prev) => [...prev, emptyOccupant()])
                }
              >
                {_t(
                  "addOccupant",
                  "Occupants step: button to add another person who will live in the property"
                )}
              </Button>

              <div
                className={`${styles.actions} ${occupantStyles.stepActions}`}
              >
                <Button
                  size="lg"
                  block
                  loading={submitLoading}
                  onClick={handleSubmit}
                >
                  {occupants.some((occupant) => !isEmptyOccupant(occupant))
                    ? _t(
                        "saveAndFinish",
                        "Occupants step: final button that saves occupants and completes company onboarding"
                      )
                    : _t(
                        "skipAndFinish",
                        "Occupants step: final button when no occupant was added; occupants are optional and can be added later from the profile"
                      )}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default OccupantsStep;
